import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  FaBook,
  FaUser,
  FaPhone,
  FaClock,
  FaChartLine,
  FaEdit,
  FaCalendarAlt,
  FaClipboardList,
  FaCheckCircle,
  FaTimesCircle,
  FaLayerGroup,
  FaQuestionCircle,
} from "react-icons/fa";

import {
  useGetQuizByIdQuery,
  useGetQuizBlueprintQuery,
  useUpdateQuizMutation,
} from "../../slices/quizApiSlice";

import QuizImageStatusCard from "../../components/quizImage/QuizImageStatusCard";

const QuizDetailsScreen = () => {
  const { id } = useParams();

  const navigate = useNavigate();

  const { data: quiz, isLoading, error } = useGetQuizByIdQuery(id);

  const { data: blueprintResponse } = useGetQuizBlueprintQuery(id);

const blueprint = blueprintResponse?.data;

  const [updateQuiz, { isLoading: publishing }] = useUpdateQuizMutation();

  const publishHandler = async () => {
    if (!blueprint) {
      toast.error("Please create quiz blueprint before publishing");

      return;
    }

    if (!blueprint.sections || blueprint.sections.length === 0) {
      toast.error("Blueprint must contain at least one section");

      return;
    }

    const confirmPublish = window.confirm(
      "Are you sure you want to publish this quiz?",
    );

    if (!confirmPublish) {
      return;
    }

    try {
      await updateQuiz({
        quizId: id,

        status: "published",
      }).unwrap();

      toast.success("Quiz published successfully");
    } catch (err) {
      toast.error(err?.data?.message || err?.error || "Failed to publish quiz");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        Loading quiz...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="rounded-xl bg-red-100 p-5 text-red-700">
          {error?.data?.message || error.error}
        </div>
      </div>
    );
  }

  const totalQuestions =
  blueprint?.sections?.reduce(
    (sum, section) => sum + Number(section.numberOfQuestions || 0),
    0,
  ) || 0;

  const totalMarks =
    blueprint?.sections?.reduce(
      (sum, section) =>
        sum +
        Number(section.numberOfQuestions || 0) *
          Number(section.marksPerQuestion || 0),
      0,
    ) || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
          <Link
            to="/quizzes"
            className="rounded-xl bg-white px-5 py-3 shadow text-blue-600"
          >
            ← Back to Quizzes
          </Link>

          <div className="flex flex-wrap gap-3">
            {quiz.status !== "published" && (
              <button
                onClick={publishHandler}
                disabled={publishing}
                className="rounded-xl bg-green-700 px-5 py-3 text-white font-semibold flex items-center gap-2 disabled:opacity-60"
              >
                <FaCheckCircle />

                {publishing ? "Publishing..." : "Publish Quiz"}
              </button>
            )}

            <button
              onClick={() => navigate(`/quizzes/${id}/edit`)}
              className="rounded-xl bg-amber-500 px-5 py-3 text-white font-semibold flex items-center gap-2"
            >
              <FaEdit />
              Edit Quiz
            </button>

            <button
              onClick={() => navigate(`/quizzes/${id}/blueprint/edit`)}
              className="rounded-xl bg-purple-600 px-5 py-3 text-white font-semibold flex items-center gap-2"
            >
              <FaLayerGroup />
              Edit Blueprint
            </button>

            <button
              onClick={() => navigate(`/quizzes/${id}/generate`)}
              className="rounded-xl bg-blue-600 px-5 py-3 text-white font-semibold flex items-center gap-2"
            >
              <FaQuestionCircle />
              Generate Questions
            </button>
          </div>
        </div>

        <div className="rounded-3xl bg-white/80 backdrop-blur-lg shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 text-white">
            <div className="flex items-center gap-4">
              <div className="rounded-2xl bg-white/20 p-4">
                <FaClipboardList className="text-4xl" />
              </div>

              <div>
                <h1 className="text-4xl font-bold">{quiz.name}</h1>

                <p className="text-blue-100 mt-2">
                  {quiz.description || "No description available"}
                </p>
              </div>
            </div>
          </div>

          <div className="p-8 pb-0">
            <QuizImageStatusCard quizId={quiz._id} />
          </div>

          <div className="grid md:grid-cols-2 gap-8 p-8">
            <div className="rounded-xl border bg-gray-50 p-5">
              <label className="text-sm uppercase text-gray-500 font-semibold">
                Subject
              </label>

              <div className="mt-3 flex gap-3 items-center text-lg font-bold">
                <FaBook className="text-blue-600" />

                {quiz.questionBankSubject?.name || quiz.subject?.name || "N/A"}
              </div>
            </div>

            <div className="rounded-xl border bg-gray-50 p-5">
              <label className="text-sm uppercase text-gray-500 font-semibold">
                Created By
              </label>

              <div className="mt-3 flex gap-3 items-center text-lg font-bold">
                <FaUser className="text-indigo-600" />

                {quiz.createdBy?.name || "N/A"}
              </div>

              {quiz.createdBy?.phone && (
                <div className="mt-2 flex gap-3">
                  <FaPhone className="text-green-600" />

                  {quiz.createdBy.phone}
                </div>
              )}
            </div>

            <div className="rounded-xl border bg-gray-50 p-5">
              <label className="uppercase text-sm text-gray-500 font-semibold">
                Duration
              </label>

              <p className="mt-3 text-lg font-bold flex gap-3">
                <FaClock className="text-green-600" />
                {quiz.durationMinutes} minutes
              </p>
            </div>

            <div className="rounded-xl border bg-gray-50 p-5">
              <label className="uppercase text-sm text-gray-500 font-semibold">
                Passing Percentage
              </label>

              <p className="mt-3 text-lg font-bold flex gap-3">
                <FaChartLine className="text-purple-600" />
                {quiz.passingPercentage}%
              </p>
            </div>

            <div className="rounded-xl border bg-gray-50 p-5">
              <label className="uppercase text-sm text-gray-500 font-semibold">
                Blueprint
              </label>

              <p className="mt-3 flex gap-3 items-center font-bold">
                <FaLayerGroup className="text-blue-600" />

                {blueprint
                  ? `${blueprint.sections?.length || 0} Sections`
                  : "No Blueprint"}
              </p>
            </div>

            <div className="rounded-xl border bg-gray-50 p-5">
              <label className="uppercase text-sm text-gray-500 font-semibold">
                Question Plan
              </label>

              <p className="mt-3 font-bold">Questions: {totalQuestions}</p>

              <p className="mt-2 font-bold">Total Marks: {totalMarks}</p>
            </div>

            <div className="rounded-xl border bg-gray-50 p-5">
              <label className="uppercase text-sm text-gray-500 font-semibold">
                Status
              </label>

              <p
                className={`mt-3 inline-flex rounded-full px-4 py-2 font-semibold ${
                  quiz.status === "published"
                    ? "bg-green-100 text-green-700"
                    : "bg-yellow-100 text-yellow-700"
                }`}
              >
                {quiz.status?.toUpperCase() || "DRAFT"}
              </p>
            </div>

            <div className="rounded-xl border bg-gray-50 p-5">
              <label className="uppercase text-sm text-gray-500 font-semibold">
                Attempts
              </label>

              <p className="mt-3 flex items-center gap-3 font-bold">
                {quiz.allowMultipleAttempts ? (
                  <>
                    <FaCheckCircle className="text-green-600" />
                    {quiz.maxAttempts} Attempts Allowed
                  </>
                ) : (
                  <>
                    <FaTimesCircle className="text-red-600" />
                    Single Attempt
                  </>
                )}
              </p>
            </div>

            <div className="rounded-xl border bg-gray-50 p-5">
              <label className="uppercase text-sm text-gray-500 font-semibold">
                Created Date
              </label>

              <p className="mt-3 flex gap-3 font-bold">
                <FaCalendarAlt className="text-indigo-600" />

                {new Date(quiz.createdAt).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizDetailsScreen;
